import { useMemo, useState, memo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, ExternalLink, Loader2, QrCode, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Select } from '@/components/ui/Input';
import { Skeleton } from '@/components/ui/Skeleton';
import { toast } from '@/components/ui/Toast';
import { eventsApi } from '@/features/events/api';
import { StationQrDialog } from '@/features/stations/components/StationQrDialog';
import { queryKeys } from '@/lib/api/queryKeys';
import { xurveyApi, xurveyPublicUrl, type XurveyDeployment } from '@/lib/api/xurvey';
import type { XkStation } from '@/lib/api/types';
import { useT } from '@/lib/i18n/useT';

/**
 * Poner una encuesta de Xurvey en marcha en las estaciones de este evento.
 *
 * Xotski **no diseña encuestas**: las usa. El diseño vive en Xurvey y lo que
 * se hace aquí es un *despliegue* — la encuesta puesta en marcha en una
 * estación concreta, con su propio enlace público. El protocolo entero está en
 * `ox-xurvey/docs/07-Protocolo-Integracion-Apps.md`; esto es su lado de Xotski.
 *
 * **Un despliegue por estación, no uno por evento.** Podría parecer más simple
 * desplegar una vez para todo el evento, pero entonces las respuestas no
 * sabrían de qué estación vinieron: se pierde el descuento de stock, el
 * monitor y la analítica por estación, que es de lo que vive Xotski.
 *
 * Desplegar dos veces la misma estación devuelve el despliegue que ya había.
 * Por eso «Desplegar en todas» se puede pulsar sin miedo tantas veces como
 * haga falta: es la forma de incorporar las estaciones nuevas de un evento que
 * ya estaba en marcha.
 */
function XurveyDeploySectionBase({
  eventId,
  surveyId,
  onSurveyId,
}: {
  eventId: number;
  /**
   * La encuesta elegida. **Controlado desde fuera a propósito**: de ella
   * cuelgan también los pasos y el móvil, y con el estado aquí dentro el
   * selector cambiaba pero el simulador seguía enseñando la anterior — que es
   * exactamente el fallo que hay que evitar en un editor.
   */
  surveyId: number | null;
  onSurveyId: (id: number) => void;
}) {
  const t = useT();
  const qc = useQueryClient();
  const [qrStation, setQrStation] = useState<number | null>(null);

  const surveysQ = useQuery({
    queryKey: ['xurvey', 'surveys', 'station'],
    queryFn: () => xurveyApi.listSurveys({ scope: 'station' }),
    staleTime: 60_000,
  });

  const stationsQ = useQuery({
    queryKey: queryKeys.stations.byEvent(eventId),
    queryFn: () => eventsApi.listStations({ eventId }),
  });

  const elegida = surveyId;

  const deploymentsQ = useQuery({
    queryKey: ['xurvey', 'deployments', elegida],
    queryFn: () => xurveyApi.listDeployments(elegida!),
    enabled: elegida != null,
  });

  const stations = (stationsQ.data?.items ?? []) as XkStation[];

  /** Despliegue por id de estación. El `entityId` viaja como texto: para
   *  Xurvey es un id opaco de otra app, no un entero suyo. */
  const porEstacion = useMemo(() => {
    const m = new Map<string, XurveyDeployment>();
    for (const d of deploymentsQ.data ?? []) m.set(String(d.entityId), d);
    return m;
  }, [deploymentsQ.data]);

  const desplegar = useMutation({
    mutationFn: async (targets: XkStation[]) => {
      const hechos: XurveyDeployment[] = [];
      // En serie y no en paralelo: el slug se calcula comprobando los que ya
      // existen, y dos altas simultáneas pelearían por el mismo.
      for (const s of targets) {
        hechos.push(
          await xurveyApi.createDeployment(elegida!, { entityId: String(s.id), name: s.name }),
        );
        // `surveysId` es la columna de Xotski que dice qué encuesta corre esta
        // estación, y de ella viven la tarjeta del QR, el quick start y la
        // cabecera de la estación. El despliegue no la escribe —Xurvey no toca
        // tablas de otras apps—, así que la anota Xotski.
        if (s.surveysId !== elegida) {
          await eventsApi.updateStation(s.id, { surveysId: elegida! });
        }
      }
      return hechos;
    },
    onSuccess: (hechos) => {
      qc.invalidateQueries({ queryKey: ['xurvey', 'deployments', elegida] });
      qc.invalidateQueries({ queryKey: queryKeys.stations.byEvent(eventId) });
      toast({ tone: 'success', title: t('xurveyDeploy.done', { n: String(hechos.length) }) });
    },
    onError: (e: Error) => toast({ tone: 'error', title: e.message }),
  });

  const pendientes = stations.filter((s) => !porEstacion.has(String(s.id)));

  if (surveysQ.isLoading) return <Skeleton className="h-40" />;

  const surveys = surveysQ.data?.items ?? [];

  return (
    <div className="space-y-3">
      <p className="text-[10px] font-bold uppercase tracking-wider text-[var(--color-fg-muted)]">
        {t('xurveyDeploy.deployHeading')}
      </p>
        {surveys.length === 0 ? (
          /*
           * Sin encuestas no se puede desplegar, y el motivo casi nunca es que
           * no existan: es que están en borrador. Se dice, porque «no hay
           * encuestas» mandaría a buscar en el sitio equivocado.
           */
          <p className="rounded-lg border border-dashed border-[var(--color-border-default)] p-6 text-center text-sm text-[var(--color-fg-muted)]">
            {t('xurveyDeploy.noSurveys')}
          </p>
        ) : (
          <>
            <div className="flex flex-col gap-2">
              <label className="flex flex-col gap-1">
                <span className="text-[10px] uppercase tracking-wide text-[var(--color-fg-muted)]">
                  {t('xurveyDeploy.surveyLabel')}
                </span>
                <Select
                  value={String(elegida ?? '')}
                  onChange={(e) => onSurveyId(Number(e.target.value))}
                >
                  {surveys.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                    </option>
                  ))}
                </Select>
              </label>

              <Button
                size="sm"
                onClick={() => desplegar.mutate(stations)}
                loading={desplegar.isPending}
                disabled={stations.length === 0 || elegida == null}
              >
                <RefreshCw className="mr-1.5 h-4 w-4" />
                {t('xurveyDeploy.deployAll')}
              </Button>
            </div>

            {stationsQ.isLoading || deploymentsQ.isLoading ? (
              <Skeleton className="h-32" />
            ) : stations.length === 0 ? (
              <p className="rounded-lg border border-dashed border-[var(--color-border-default)] p-6 text-center text-sm text-[var(--color-fg-muted)]">
                {t('xurveyDeploy.noStations')}
              </p>
            ) : (
              <>
                {pendientes.length > 0 ? (
                  <p className="text-xs text-[var(--color-fg-muted)]">
                    {t('xurveyDeploy.pending', { n: String(pendientes.length) })}
                  </p>
                ) : null}

                <ul className="divide-y divide-[var(--color-border-default)] rounded-lg border border-[var(--color-border-default)]">
                  {stations.map((s) => {
                    const dep = porEstacion.get(String(s.id));
                    return (
                      <li key={s.id} className="flex items-center gap-1.5 px-2 py-1.5">
                        {dep ? (
                          <Check className="h-3.5 w-3.5 flex-none text-[var(--color-success-fg,green)]" />
                        ) : null}
                        <span
                          className="min-w-0 flex-1 truncate text-[13px]"
                          // El slug en el título y no en la fila: es largo, se
                          // repite en todas y en una columna estrecha se come
                          // el nombre, que es lo único que se busca aquí.
                          title={dep?.slug ?? undefined}
                        >
                          {s.name}
                        </span>

                        {dep ? (
                          <>
                            <Button
                              variant="ghost"
                              size="sm"
                              title={t('xurveyDeploy.open')}
                              onClick={() => window.open(xurveyPublicUrl(dep.slug), '_blank')}
                            >
                              <ExternalLink className="h-3.5 w-3.5" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              title={t('xurveyDeploy.qr')}
                              onClick={() => setQrStation(s.id)}
                            >
                              <QrCode className="h-3.5 w-3.5" />
                            </Button>
                          </>
                        ) : (
                          <Button
                            variant="secondary"
                            size="sm"
                            disabled={desplegar.isPending}
                            onClick={() => desplegar.mutate([s])}
                          >
                            {desplegar.isPending ? (
                              <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
                            ) : null}
                            {t('xurveyDeploy.deployOne')}
                          </Button>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </>
            )}
          </>
        )}

      {qrStation != null ? (
        <StationQrDialog
          open
          onOpenChange={(v) => !v && setQrStation(null)}
          stationId={qrStation}
          xurveyUrl={(() => {
            const dep = porEstacion.get(String(qrStation));
            return dep ? xurveyPublicUrl(dep.slug) : null;
          })()}
        />
      ) : null}
    </div>
  );
}

/**
 * Memorizado a propósito. Elegir encuesta y ver enlaces no tiene nada que ver con un color; sin esto se rehacía en cada tecla.
 * Sus manejadores llegan estables desde `DesignJourneySection`, que es lo que
 * hace que esto sirva de algo.
 */
export const XurveyDeploySection = memo(XurveyDeploySectionBase);
